import {
  type FC,
  type ChangeEvent,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { Input } from 'antd';
import { debounce } from 'lodash';
import { availableBillableMinutesToUnregisterReadings } from 'infrastructure/services/interactiveTime';
import { useTranslation } from 'react-i18next';
import useUpdateEffect from 'infrastructure/hooks/utils/use-update-effect';

import s from './styles.module.scss';

import type {
  Filters,
  AppendFilters,
} from 'infrastructure/hooks/interactiveTimeDashboard/types';

interface Props {
  filters: Filters;
  appendFilters: AppendFilters;
}

const AvailableBillableMinutes: FC<Props> = ({ appendFilters, filters }) => {
  const { t } = useTranslation();

  const [key, setKey] = useState<string>(`${Date.now()}`);

  const debouncedAppendFilters = useMemo(
    () =>
      debounce(
        (
          e: ChangeEvent<HTMLInputElement>,
          field:
            | 'agencyAvailableBillableMinutesMin'
            | 'agencyAvailableBillableMinutesMax',
        ) => {
          const value = availableBillableMinutesToUnregisterReadings(
            e.target.value,
          );
          if (!e.target.value) {
            appendFilters({
              [field]: undefined,
            });
          } else {
            appendFilters({
              [field]: {
                inputValue: e.target.value || undefined,
                value,
              },
            });
          }
        },
        300,
      ),
    [appendFilters],
  );

  const handleMinChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      debouncedAppendFilters(e, 'agencyAvailableBillableMinutesMin');
    },
    [debouncedAppendFilters],
  );

  const handleMaxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      debouncedAppendFilters(e, 'agencyAvailableBillableMinutesMax');
    },
    [debouncedAppendFilters],
  );

  useUpdateEffect(() => {
    if (
      !filters.agencyAvailableBillableMinutesMin?.inputValue &&
      !filters.agencyAvailableBillableMinutesMax?.inputValue
    ) {
      setKey(`${Date.now()}`);
    }
  }, [filters]);

  return (
    <div className={s['available-billable-minutes-wrapper']}>
      <label
        htmlFor="availableBillableMinutesMin"
        data-cy="interactiveTimeDashboard-filter-availableBillableMinutes"
      >
        {t('labels.availableBillableMinutes')}
      </label>
      <div className={s['input-wrapper']} key={key}>
        <Input
          onWheel={(e) => e.currentTarget.blur()}
          id="availableBillableMinutesMin"
          placeholder="Min"
          type="number"
          defaultValue={filters.agencyAvailableBillableMinutesMin?.inputValue}
          onChange={handleMinChange}
        />
        <Input
          onWheel={(e) => e.currentTarget.blur()}
          id="agencyAvailableBillableMinutesMax"
          placeholder="Max"
          type="number"
          defaultValue={filters.agencyAvailableBillableMinutesMax?.inputValue}
          onChange={handleMaxChange}
        />
      </div>
    </div>
  );
};

export default AvailableBillableMinutes;
